// Argus — FolderTreeView: Windows-Explorer-style folder tree with lazy load on expand.
// Renders directories only (skips files). Single click navigates the right pane.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import StorageIcon from '@mui/icons-material/Storage';
import FolderIcon from '@mui/icons-material/Folder';
import { fetchFsEntries, refreshFsEntriesViaWs, iconUrl, joinPath, formatEntryDisplayName, type FsEntry } from '../../lib/fsEntries';
import { iconCache } from '../../lib/iconCache';

export interface FolderTreeViewProps {
  agentId: string;
  currentPath: string;
  onNavigate: (path: string) => void;
}

interface NodeState {
  entries: FsEntry[];
  loading: boolean;
  error: string | null;
}

export function FolderTreeView({ agentId, currentPath, onNavigate }: FolderTreeViewProps): JSX.Element {
  const [nodesByPath, setNodesByPath] = useState<Map<string, NodeState>>(() => new Map());
  const [expandedIds, setExpandedIds] = useState<string[]>(() => deriveAncestors(currentPath));
  const containerRef = useRef<HTMLDivElement | null>(null);

  const loadPath = useCallback(async (path: string): Promise<void> => {
    setNodesByPath((prev) => {
      const next = new Map(prev);
      const existing = next.get(path);
      next.set(path, { entries: existing?.entries ?? [], loading: true, error: null });
      return next;
    });
    try {
      // WS direct — items returnate inline. La WS error fallback la REST cache.
      let items: FsEntry[];
      try {
        items = await refreshFsEntriesViaWs(agentId, path);
      } catch {
        const fallback = await fetchFsEntries(agentId, path);
        items = [...fallback.items];
      }
      setNodesByPath((prev) => {
        const next = new Map(prev);
        next.set(path, { entries: items, loading: false, error: null });
        return next;
      });
    } catch (err) {
      setNodesByPath((prev) => {
        const next = new Map(prev);
        next.set(path, { entries: [], loading: false, error: err instanceof Error ? err.message : 'Failed to load' });
        return next;
      });
    }
  }, [agentId]);

  // Initial load: drives (path='').
  useEffect(() => {
    if (!nodesByPath.has('')) {
      void loadPath('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-expand path to currentPath when it changes (e.g. user navigates from breadcrumb).
  useEffect(() => {
    const ancestors = deriveAncestors(currentPath);
    setExpandedIds((prev) => Array.from(new Set([...prev, ...ancestors])));
    // Pre-load entries for ancestors that aren't loaded yet.
    for (const a of ancestors) {
      if (!nodesByPath.has(a)) void loadPath(a);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  // Scroll selected node into view once it lands in the DOM.
  // Tree data loads async (drives → cascade ancestors), and SimpleTreeView's internal
  // aria-selected effect may also lag a render. Combine MutationObserver (catches DOM
  // mutations as ancestors load cascade) with rAF retry as fallback for browsers that
  // mutate before observer attaches. Total timeout: 5s. Fires once per currentPath.
  useEffect(() => {
    if (currentPath === '') return;
    const container = containerRef.current;
    if (container === null) return;

    let done = false;
    let rafId: number | null = null;
    let observer: MutationObserver | null = null;
    let timeoutId: number | null = null;
    const MAX_MS = 5000;
    const STEP_MS = 32;

    const tryScroll = (): boolean => {
      if (done) return true;
      const el = container.querySelector('[role="treeitem"][aria-selected="true"]') as HTMLElement | null;
      if (el === null) return false;
      // Verify the element is actually rendered (offsetParent !== null means visible in layout).
      if (el.offsetParent === null) return false;
      done = true;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return true;
    };

    // Pass 1: try immediately (cache hit case).
    if (tryScroll()) return;

    // Pass 2: MutationObserver — detect when DOM grows (ancestor load cascade adds treeitems).
    observer = new MutationObserver(() => {
      if (tryScroll()) {
        observer?.disconnect();
        if (rafId !== null) window.cancelAnimationFrame(rafId);
        if (timeoutId !== null) window.clearTimeout(timeoutId);
      }
    });
    observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-selected', 'aria-expanded'] });

    // Pass 3: rAF retry (covers gaps where MutationObserver doesn't fire — e.g., Virtuoso-like layout).
    let elapsed = 0;
    const rafLoop = () => {
      if (done) return;
      if (tryScroll()) return;
      elapsed += STEP_MS;
      if (elapsed < MAX_MS) {
        rafId = window.requestAnimationFrame(rafLoop);
      }
    };
    rafId = window.requestAnimationFrame(rafLoop);

    // Hard timeout cleanup.
    timeoutId = window.setTimeout(() => {
      done = true;
      observer?.disconnect();
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    }, MAX_MS);

    return () => {
      done = true;
      observer?.disconnect();
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [currentPath]);

  const handleExpansion = (_event: React.SyntheticEvent | null, ids: unknown) => {
    if (!Array.isArray(ids)) return;
    const sids = ids.filter((x): x is string => typeof x === 'string');
    const newlyExpanded = sids.filter((id) => !expandedIds.includes(id));
    setExpandedIds(sids);
    for (const id of newlyExpanded) {
      if (!nodesByPath.has(id)) void loadPath(id);
    }
  };


  const handleSelect = (_event: React.SyntheticEvent | null, id: unknown) => {
    let resolved: string | null = null;
    if (typeof id === 'string') resolved = id;
    else if (Array.isArray(id) && id.length > 0 && typeof id[0] === 'string') resolved = id[0];
    if (resolved === null || resolved === '') return;
    onNavigate(resolved);
  };

  // Render top-level (drives root).
  const root = nodesByPath.get('');

  return (
    <Box ref={containerRef} sx={{ height: '100%', overflow: 'auto', p: 0.5 }}>
      {root === undefined || root.loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">Loading drives…</Typography>
        </Box>
      ) : root.error ? (
        <Typography variant="caption" color="error" sx={{ p: 1 }}>{root.error}</Typography>
      ) : (
        <SimpleTreeView
          expandedItems={expandedIds}
          selectedItems={currentPath !== '' ? currentPath : null}
          onExpandedItemsChange={handleExpansion}
          onSelectedItemsChange={handleSelect}
          expansionTrigger="iconContainer"
          sx={{ '& .MuiTreeItem-content': { py: 0.25, fontSize: 13 } }}
        >
          {/* Defense-in-depth: dedup drive entries by full path (treeitem itemId must be unique). */}
          {Array.from(new Map(root.entries.map((d) => [driveFullPath(d.name), d])).values()).map((drive) => (
            <FolderNode
              key={driveFullPath(drive.name)}
              entry={drive}
              fullPath={driveFullPath(drive.name)}
              nodesByPath={nodesByPath}
              agentId={agentId}
              isDrive
            />
          ))}
        </SimpleTreeView>
      )}
    </Box>
  );
}

interface FolderNodeProps {
  entry: FsEntry;
  fullPath: string;
  nodesByPath: Map<string, NodeState>;
  agentId: string;
  isDrive?: boolean;
}

function FolderNode({ entry, fullPath, nodesByPath, isDrive = false, agentId }: FolderNodeProps): JSX.Element {
  const childState = nodesByPath.get(fullPath);
  const childFolders = (childState?.entries ?? []).filter((e) => e.isDir);
  const FallbackIcon = isDrive ? StorageIcon : FolderIcon;
  const displayLabel = isDrive ? formatEntryDisplayName(entry) : entry.name;

  // Iconița reală Windows din WS payload (bloc ws-icons-inline). Fallback la REST size=20 când
  // cache memory miss (offline sau hash necunoscut). Final fallback: MUI generic Storage/Folder.
  const bundle = entry.iconHash !== null ? iconCache.getBundle(entry.iconHash) : null;
  const realIconSrc = bundle
    ? bundle.url20
    : entry.iconHash !== null
      ? `${iconUrl(agentId, entry.iconHash)}?size=20`
      : null;

  // SimpleTreeView are `expansionTrigger="iconContainer"` setat la nivel root — click pe chevron
  // (▶/▼ stânga) = expand toggle FĂRĂ select; click pe content (icon Storage/Folder + nume) =
  // select + populate panel. Pattern-ul oficial MUI X (vezi docs Tree View Expansion).
  const label = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      {realIconSrc !== null ? (
        <Box
          component="img"
          src={realIconSrc}
          alt=""
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
          sx={{ width: 16, height: 16, objectFit: 'contain', flexShrink: 0, display: 'block' }}
        />
      ) : (
        <FallbackIcon
          sx={{ fontSize: 16, color: isDrive ? 'primary.main' : 'warning.main', flexShrink: 0 }}
        />
      )}
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={displayLabel}>
        {displayLabel}
      </Box>
    </Box>
  );

  // Ensure expand arrow shows even when children not yet loaded — render a placeholder TreeItem.
  if (childState === undefined) {
    return (
      <TreeItem itemId={fullPath} label={label}>
        <TreeItem itemId={`${fullPath}::__placeholder__`} label={
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Loading…</Typography>
        } />
      </TreeItem>
    );
  }

  if (childState.loading && childFolders.length === 0) {
    return (
      <TreeItem itemId={fullPath} label={label}>
        <TreeItem itemId={`${fullPath}::__loading__`} label={
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>Loading…</Typography>
        } />
      </TreeItem>
    );
  }

  if (childState.error) {
    return (
      <TreeItem itemId={fullPath} label={label}>
        <TreeItem itemId={`${fullPath}::__error__`} label={
          <Typography variant="caption" color="error" sx={{ fontStyle: 'italic' }}>{childState.error}</Typography>
        } />
      </TreeItem>
    );
  }

  if (childFolders.length === 0) {
    // Leaf — no folders inside.
    return <TreeItem itemId={fullPath} label={label} />;
  }

  return (
    <TreeItem itemId={fullPath} label={label}>
      {childFolders.map((child) => (
        <FolderNode
          key={child.name}
          entry={child}
          fullPath={joinPath(fullPath, child.name)}
          nodesByPath={nodesByPath}
          agentId={agentId}
        />
      ))}
    </TreeItem>
  );
}

function driveFullPath(driveName: string): string {
  // Drive entries arrive with name like "C:" — full path is "C:\".
  if (/^[A-Za-z]:$/.test(driveName)) return `${driveName}\\`;
  return driveName;
}

function deriveAncestors(path: string): string[] {
  if (path === '') return [];
  const ancestors: string[] = [];
  // Drive root: "C:\" → [""...] but tree starts after root list. Add drive segment.
  const m = /^([A-Za-z]:)\\?(.*)$/.exec(path);
  if (!m) return [];
  const drive = `${m[1]}\\`;
  ancestors.push(drive);
  const rest = m[2] ?? '';
  if (rest === '') return ancestors;
  const segments = rest.split('\\').filter((s) => s.length > 0);
  let acc = drive;
  for (const seg of segments) {
    acc = acc.endsWith('\\') ? `${acc}${seg}` : `${acc}\\${seg}`;
    ancestors.push(acc);
  }
  return ancestors;
}
